'use client'
import * as Popover from '@radix-ui/react-popover'
import { Portal as RadixPortal } from '@radix-ui/react-portal'
import { useWindowDomEvent } from 'hooks/use-dom-event'
import * as React from 'react'
import { usePortalContainerRef } from '../portal/portal-container'
import { callAll } from 'utils/call-all'
import { tw } from 'utils/classnames'
import { safePolygon } from './safe-polygon'
import type { Polygon, Side } from './safe-polygon'

// eslint-disable-next-line no-barrel-files/no-barrel-files
// oxlint-disable-next-line cf-web/no-barrel-file
export * from '@radix-ui/react-popover'

const showSafeArea =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('showSafeArea')

// oxlint-disable-next-line cf-web/no-slop-comments
// Sibling coordination for hover popovers. When a user rapidly moves across
// sibling triggers (studio nav clusters) with CPU throttling active (e.g.
// DevTools open), React's commit for `setOpen(true)` and the follow-up
// `useLayoutEffect` that activates `blockPointerEvents` can lag behind the
// queued `mouseenter` events. Each sibling ends up calling `setOpen(true)`
// against an unblocked body, leaving several popovers open at once — one gets
// stuck and neighbouring triggers stop expanding. `blockPointerEvents` alone
// cannot fix this: it only takes effect after the second commit (Presence
// mounts the portaled content in a follow-up commit).
//
// Every hover root registers on mount, before it can receive pointer input.
// `active` is updated synchronously inside the trigger handlers, so it also
// represents an open intent that React has not committed yet. Nested popovers
// stay unaffected: an entry is skipped whenever the incoming trigger sits
// inside its own trigger or content subtree.
interface HoverPopoverEntry {
  triggerRef: React.RefObject<HTMLButtonElement | null>
  contentRef: React.RefObject<HTMLDivElement | null>
  close: () => void
  active: boolean
}

const hoverPopovers = new Set<HoverPopoverEntry>()

function activateHoverPopover(entry: HoverPopoverEntry, source: Element | null) {
  if (!source) {
    return
  }

  for (const otherEntry of hoverPopovers) {
    if (otherEntry === entry || !otherEntry.active) {
      continue
    }

    const trigger = otherEntry.triggerRef.current
    const content = otherEntry.contentRef.current
    if (trigger === source || trigger?.contains(source) || content?.contains(source)) {
      continue
    }

    otherEntry.active = false
    otherEntry.close()
  }

  entry.active = true
}

type Behaviour = 'click' | 'hover' | 'both'
type FocusBehaviour = 'focus' | 'none'
type RootProps = Popover.PopoverProps & {
  id?: string
  behaviour?: Behaviour
  focusBehaviour?: FocusBehaviour
  blockPointerEvents?: boolean
  safePolygonBulge?: number
  safePolygonRequireIntent?: boolean
  safePolygonRestMs?: number
}

interface PopoverContextValue {
  open: boolean
  setOpen(value: boolean): void
  behaviour?: Behaviour
  focusBehaviour?: FocusBehaviour
  triggerRef?: React.RefObject<HTMLButtonElement | null>
  contentRef?: React.RefObject<HTMLDivElement | null>
  /** Starts the safe-polygon close check after the cursor leaves the trigger or content. */
  startSafePolygonCheck?: (event: MouseEvent) => void
  /** Stops the safe-polygon close check (cursor landed on trigger/content, or popover closed). */
  stopSafePolygonCheck?: () => void
  /** Activates pointer blocking once the portaled content ref has mounted. */
  syncPointerEventsBlock?: () => void
  /** Marks this hover root active and closes active siblings before React commits. */
  activateHoverPopover?: (source: Element | null) => void
}

const PopoverContext = React.createContext<PopoverContextValue | null>(null)

export function usePopoverContext() {
  const context = React.useContext(PopoverContext)

  if (!context) {
    throw new Error(`usePopoverContext() must be called inside <Popover.Root />`)
  }

  return context
}

export function Portal({ container: containerProp, ...props }: Popover.PopoverPortalProps) {
  const portalContainerRef = usePortalContainerRef()
  // Modal dialogs whitelist only their content element for scroll; portaling to document.body
  // lands outside that region and wheel events get swallowed. An ancestor can opt in via
  // PortalContainerProvider — explicit `container` still wins.
  const container = containerProp ?? portalContainerRef?.current ?? undefined

  return <Popover.Portal container={container} {...props} />
}

export function Root({
  behaviour = 'click',
  focusBehaviour = 'focus',
  blockPointerEvents = false,
  safePolygonBulge = 0,
  safePolygonRequireIntent = true,
  safePolygonRestMs = 40,
  ...props
}: RootProps) {
  const [open, setOpen] = React.useState(false)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const contentRef = React.useRef<HTMLDivElement>(null)
  const [debugPolygons, setDebugPolygons] = React.useState<Polygon[] | null>(null)

  const isControlled = props.open !== undefined
  const currentOpen = isControlled ? props.open : open

  // The close function changes between renders (controlled vs uncontrolled),
  // but the safe-polygon mousemove handler must always call the latest one.
  const setCurrentOpen = isControlled ? props.onOpenChange : setOpen
  const closeRef = React.useRef<() => void>(() => {})
  const [hoverPopoverEntry] = React.useState<HoverPopoverEntry>(() => ({
    triggerRef,
    contentRef,
    close: () => closeRef.current(),
    active: false,
  }))
  closeRef.current = () => {
    hoverPopoverEntry.active = false
    setCurrentOpen?.(false)
  }

  const activateCurrentHoverPopover = React.useCallback(
    (source: Element | null) => activateHoverPopover(hoverPopoverEntry, source),
    [hoverPopoverEntry],
  )

  // One safePolygon instance per popover: it keeps cursor-intent state
  // (speed, whether the cursor landed on the content) across leave events.
  // Recreated when polygon options change so dynamic prop updates take effect;
  // the cleanup effect below releases the old instance's body guard.
  const createSafePolygonHandler = React.useMemo(
    () =>
      safePolygon({
        blockPointerEvents,
        bulge: safePolygonBulge,
        requireIntent: safePolygonRequireIntent,
        restMs: safePolygonRestMs,
        debug: showSafeArea ? polygons => setDebugPolygons(polygons) : undefined,
      }),
    [blockPointerEvents, safePolygonBulge, safePolygonRequireIntent, safePolygonRestMs],
  )

  const syncPointerEventsBlock = React.useCallback(() => {
    const trigger = triggerRef.current
    const content = contentRef.current
    if (trigger && content) {
      createSafePolygonHandler.blockOutsidePointerEvents(trigger, content)
    }
  }, [createSafePolygonHandler])

  const safePolygonCheckRef = React.useRef<{ unbind: () => void } | null>(null)

  const stopSafePolygonCheck = React.useCallback(() => {
    safePolygonCheckRef.current?.unbind()
    safePolygonCheckRef.current = null
    // Also clears the pending 40ms rest-timeout, which would otherwise fire
    // after the cursor already landed on the content and close the popover.
    createSafePolygonHandler.cancel()
    if (showSafeArea) {
      setDebugPolygons(null)
    }
  }, [createSafePolygonHandler])

  const startSafePolygonCheck = React.useCallback(
    (event: MouseEvent) => {
      stopSafePolygonCheck()

      const trigger = triggerRef.current
      const content = contentRef.current
      if (!trigger || !content) {
        closeRef.current()
        return
      }

      const side = (content.dataset.side ?? 'bottom') as Side
      const handler = createSafePolygonHandler({
        x: event.clientX,
        y: event.clientY,
        side,
        reference: trigger,
        floating: content,
        onClose() {
          stopSafePolygonCheck()
          closeRef.current()
        },
      })

      document.addEventListener('mousemove', handler)
      safePolygonCheckRef.current = {
        unbind: () => document.removeEventListener('mousemove', handler),
      }
      // Run once for the leave event itself: it syncs the handler's internal
      // state and closes immediately when the cursor left in a direction that
      // can never reach the content (e.g. the opposite side).
      handler.leave(event)
    },
    [createSafePolygonHandler, stopSafePolygonCheck],
  )

  const context = React.useMemo(() => {
    if (isControlled) {
      return {
        open: props.open!,
        setOpen: props.onOpenChange!,
        behaviour,
        focusBehaviour,
        triggerRef,
        contentRef,
        startSafePolygonCheck,
        stopSafePolygonCheck,
        syncPointerEventsBlock,
        activateHoverPopover: activateCurrentHoverPopover,
      }
    }

    return {
      open,
      setOpen,
      behaviour,
      focusBehaviour,
      triggerRef,
      contentRef,
      startSafePolygonCheck,
      stopSafePolygonCheck,
      syncPointerEventsBlock,
      activateHoverPopover: activateCurrentHoverPopover,
    }
  }, [
    activateCurrentHoverPopover,
    behaviour,
    focusBehaviour,
    isControlled,
    open,
    props.onOpenChange,
    props.open,
    startSafePolygonCheck,
    stopSafePolygonCheck,
    syncPointerEventsBlock,
  ])

  const rootProps = React.useMemo(() => {
    if (isControlled) {
      return props
    }

    return {
      ...props,
      open,
      onOpenChange: setOpen,
    }
  }, [isControlled, open, props])

  React.useLayoutEffect(() => {
    if (!currentOpen) {
      hoverPopoverEntry.active = false
      stopSafePolygonCheck()
      return
    }

    hoverPopoverEntry.active = true

    // Radix's Presence component can mount portaled content in a follow-up
    // commit, so ContentRoot repeats this once its own ref is available.
    syncPointerEventsBlock()

    return () => createSafePolygonHandler.unblockOutsidePointerEvents()
  }, [
    createSafePolygonHandler,
    currentOpen,
    hoverPopoverEntry,
    stopSafePolygonCheck,
    syncPointerEventsBlock,
  ])

  // Register before pointer input can arrive. Waiting for `currentOpen` would
  // miss siblings whose open updates are still queued in React.
  React.useLayoutEffect(() => {
    if (!hasHoverBehaviour(behaviour)) {
      return
    }
    hoverPopovers.add(hoverPopoverEntry)
    return () => {
      hoverPopoverEntry.active = false
      hoverPopovers.delete(hoverPopoverEntry)
    }
  }, [behaviour, hoverPopoverEntry])

  React.useEffect(
    () => () => {
      stopSafePolygonCheck()
      createSafePolygonHandler.unblockOutsidePointerEvents()
    },
    [createSafePolygonHandler, stopSafePolygonCheck],
  )

  // Close when the cursor leaves the page entirely — no mousemove will ever
  // fire again, so the safe-polygon check could never resolve.
  React.useEffect(() => {
    if (!currentOpen || !hasHoverBehaviour(behaviour)) {
      return
    }
    const html = document.documentElement
    const onLeave = () => {
      stopSafePolygonCheck()
      closeRef.current()
    }
    html.addEventListener('mouseleave', onLeave)
    return () => html.removeEventListener('mouseleave', onLeave)
  }, [currentOpen, behaviour, stopSafePolygonCheck])

  React.useEffect(() => {
    const observer = new ResizeObserver(() => {
      try {
        const isVisible = triggerRef.current?.checkVisibility()
        if (!isVisible) {
          closeRef.current()
        }
      } catch {}
    })

    triggerRef.current && observer.observe(triggerRef.current)

    return () => observer.disconnect()
  }, [])

  return (
    <PopoverContext.Provider value={context}>
      <Popover.Root {...rootProps} data-popover-root />
      {showSafeArea && currentOpen && debugPolygons ? (
        <DebugPolygonOverlay polygons={debugPolygons} />
      ) : null}
    </PopoverContext.Provider>
  )
}

function TriggerRoot(
  props: Popover.PopoverTriggerProps & {
    ref?: React.Ref<HTMLButtonElement | null>
  },
) {
  const { ref, ...restProps } = props
  const ctx = usePopoverContext()
  const setOpen = ctx?.setOpen
  const open = ctx?.open
  const behaviour = ctx?.behaviour
  const focusBehaviour = ctx?.focusBehaviour
  const isKeyPress = React.useRef(false)
  const triggerRef = ctx?.triggerRef
  const startSafePolygonCheck = ctx?.startSafePolygonCheck
  const stopSafePolygonCheck = ctx?.stopSafePolygonCheck
  const activateCurrentHoverPopover = ctx?.activateHoverPopover

  React.useEffect(() => {
    if (!open && focusBehaviour === 'none') {
      setTimeout(() => {
        triggerRef?.current?.blur()
      }, 1)
    }
  }, [open, focusBehaviour, triggerRef])

  React.useImperativeHandle<HTMLButtonElement | null, HTMLButtonElement | null>(
    ref,
    () => triggerRef?.current ?? null,
  )

  function onMouseLeave(e: React.MouseEvent) {
    startSafePolygonCheck?.(e.nativeEvent)
  }

  return (
    <Popover.Trigger
      {...restProps}
      ref={triggerRef}
      onClick={e => {
        // prevent the mouse click from closing the popover when
        // in the 'hover' mode. otherwise a user can click on an
        // a trigger whilst it is open, and it will close the modal
        if (behaviour === 'hover' && !isKeyPress.current) {
          e.preventDefault()
        }
        isKeyPress.current = false
      }}
      data-popover-trigger
      onKeyDown={() => (isKeyPress.current = true)}
      onMouseEnter={callAll(
        hasHoverBehaviour(behaviour) &&
          (() => {
            activateCurrentHoverPopover?.(triggerRef?.current ?? null)
            setOpen?.(true)
            stopSafePolygonCheck?.()
          }),
        hasHoverBehaviour(behaviour) &&
          focusBehaviour === 'focus' &&
          (() => triggerRef?.current?.focus()),
        restProps.onMouseEnter,
      )}
      onMouseLeave={callAll(hasHoverBehaviour(behaviour) && onMouseLeave, restProps.onMouseLeave)}
    />
  )
}

function hasHoverBehaviour(behaviour?: Behaviour) {
  return behaviour === 'hover' || behaviour === 'both'
}

function isNode(value: EventTarget | null): value is Node {
  return typeof Node !== 'undefined' && value instanceof Node
}

export const Trigger = TriggerRoot

// eslint-disable-next-line import/namespace
interface ContentRootProps extends Popover.PopoverContentProps {
  enableClickToClose?: boolean
  preventClickOutsideClose?: boolean
  ref?: React.Ref<HTMLDivElement | null>
}

const SIDE_HITBOX: Record<string, string> = {
  right:
    'after:absolute after:-left-[--hitbox-size] after:top-0 after:h-full after:w-[--hitbox-size] after:content-[""]',
  left: 'after:absolute after:-right-[--hitbox-size] after:top-0 after:h-full after:w-[--hitbox-size] after:content-[""]',
  bottom:
    'after:absolute after:-top-[--hitbox-size] after:left-0 after:h-[--hitbox-size] after:w-full after:content-[""]',
  top: 'after:absolute after:-bottom-[--hitbox-size] after:left-0 after:h-[--hitbox-size] after:w-full after:content-[""]',
}

function sideHitbox(side?: string) {
  return SIDE_HITBOX[side ?? 'bottom'] ?? null
}

// enableClickToClose is a workaround to make the popover stay open on very specific scenarios
// in almost every case you shouldn't need it
function ContentRoot({
  children,
  enableClickToClose = true,
  preventClickOutsideClose = false,
  ref,
  ...props
}: ContentRootProps) {
  const {
    behaviour,
    contentRef,
    open,
    setOpen,
    startSafePolygonCheck,
    stopSafePolygonCheck,
    syncPointerEventsBlock,
    triggerRef,
  } = usePopoverContext()
  const isMouseDown = React.useRef(false)
  const isClickInside = React.useRef(false)

  React.useLayoutEffect(() => {
    // Gate on open: with forceMount the Content mounts while the root is
    // closed, and acquiring the body guard here would strand the page at
    // pointer-events: none because the Root cleanup only runs on close→open
    // transitions. Only sync the block when the popover is actually open.
    if (!open) {
      return
    }
    syncPointerEventsBlock?.()
  }, [open, syncPointerEventsBlock])

  useWindowDomEvent(
    'mousedown',
    React.useCallback(() => (isMouseDown.current = true), []),
  )

  useWindowDomEvent(
    'mouseup',
    React.useCallback(() => (isMouseDown.current = false), []),
  )

  useWindowDomEvent(
    'mousedown',
    React.useCallback(
      (e: Event) => {
        if (
          enableClickToClose &&
          isNode(e.target) &&
          contentRef?.current &&
          !contentRef.current.contains(e.target) &&
          triggerRef?.current &&
          !triggerRef.current.contains(e.target) &&
          preventClickOutsideClose &&
          !isClickInside.current
        ) {
          setOpen?.(false)
        }
        isClickInside.current = false
      },
      [enableClickToClose, contentRef, triggerRef, setOpen, preventClickOutsideClose],
    ),
  )

  React.useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(
    ref,
    () => contentRef?.current ?? null,
  )

  const sideOffset = props.sideOffset || 0

  return (
    <Popover.Content
      side="bottom"
      // prevent autofocus on popovers for now since it gives them the outline
      onOpenAutoFocus={e => e.preventDefault()}
      {...props}
      ref={contentRef}
      onCloseAutoFocus={callAll(
        hasHoverBehaviour(behaviour) && ((e: Event) => e.preventDefault()),
        props.onCloseAutoFocus,
      )}
      onMouseEnter={callAll(
        hasHoverBehaviour(behaviour) &&
          (() => {
            if (!isMouseDown.current) {
              setOpen?.(true)
              stopSafePolygonCheck?.()
            }
          }),
        props.onMouseEnter,
      )}
      onMouseLeave={callAll(
        hasHoverBehaviour(behaviour) &&
          ((e: React.MouseEvent) => {
            if (!isMouseDown.current) {
              const related = e.relatedTarget
              if (isNode(related) && triggerRef?.current?.contains(related)) {
                return
              }
              // The safe-polygon trough keeps the popover open while the
              // cursor travels back toward the trigger; any other direction
              // closes it.
              startSafePolygonCheck?.(e.nativeEvent)
            }
          }),
        props.onMouseLeave,
      )}
      // the after pseudo element acts as a kind of hitbox, and we want the hitbox
      // to start from the top of the content, to the bottom of the trigger. we know this gap
      // from the sideOffset prop, so we set the height of the after element to that value
      style={{
        ...props.style,
        '--hitbox-size': sideOffset ? `${sideOffset + 1}px` : '0px', // +1 for good measure/safety
      }}
      data-popover-content
      onClickCapture={() => (isClickInside.current = true)}
      className={tw(
        // zindex on all
        'relative z-50',
        // no outline on popover contents
        'outline-none',
        // max height is max available space
        'max-h-[calc(var(--radix-popover-content-available-height)-8px)]',

        // hitbox css, only apply if there is a sideOffset
        sideOffset ? sideHitbox(props.side) : null,

        // ensure hitbox is above the content
        'after:z-10',
        props.className,
      )}
    >
      {children}
    </Popover.Content>
  )
}

export const Content = ContentRoot

// --- Mouse safe area ---
//
// The close behaviour for hover popovers is handled by a port of Floating
// UI's `safePolygon` (see ./safe-polygon.ts). When the cursor leaves the
// trigger or content, a document mousemove handler keeps the popover open
// while the cursor stays inside a polygon spanning from its exit point to
// the content, and closes it otherwise. Unlike the previous implementation
// this is purely computational — no invisible element blocks pointer
// events, so nearby triggers keep working while the cursor traverses the
// safe area.
//
// Add `?showSafeArea` to the URL to visualise the polygons.

function DebugPolygonOverlay({ polygons }: { polygons: Polygon[] }) {
  return (
    <RadixPortal className="fixed inset-0 w-full h-full pointer-events-none z-[100]">
      <svg width="100%" height="100%" className="block overflow-visible">
        {polygons.map((polygon, index) => (
          <polygon
            key={index}
            fill="rgba(255,0,0,0.2)"
            stroke="rgba(255,0,0,0.6)"
            strokeWidth="1"
            points={polygon.map(([x, y]) => `${x},${y}`).join(' ')}
          />
        ))}
      </svg>
    </RadixPortal>
  )
}
